import { useState, useEffect, useMemo, memo } from "react";
import { motion, animate, useMotionValue, useTransform } from "motion/react";
import { interpolate } from "../../../internal/flubber";
import { usePrefersReducedMotion } from "../../../hooks";
import { useTheme } from "../../../providers";

// SVG path constants
const SHARP_PATH =
  "m23 12-8.036-3.23L12.006 0 9.047 8.77 1 12l8.047 3.23L12.006 24l2.958-8.77z";
const ROUND_PATH =
  "M17.425 13.854c1.665-.675 1.665-3.033 0-3.708l-2.73-1.106-.793-2.37c-.609-1.82-3.184-1.82-3.793 0l-.793 2.37-2.734 1.106c-1.666.674-1.666 3.034 0 3.708l2.734 1.106.793 2.37c.609 1.82 3.184 1.82 3.793 0l.793-2.37z";

// Inline style constants
const SVG_STYLE = { width: "100%", height: "100%", display: "block" } as const;

// Stable gradient ID — safe to reuse across instances because the gradient is
// defined inside each individual <svg>, scoping it to that element's coordinate space.
const GRADIENT_ID = "ai-mark-gradient";

/**
 * Props for the SVGMorph component
 * @property {string[]} paths - Array of SVG path data strings to morph between
 * @property {number} [delay] - Delay in seconds before starting the animation
 * @property {boolean} [inheritColor] - Whether to inherit color from parent instead of using gradient
 */
type SVGMorphProps = {
  paths: string[];
  delay?: number;
  inheritColor?: boolean;
};

/**
 * SVGMorph component that animates morphing between multiple SVG paths.
 *
 * Features:
 * - Smooth morphing animation between paths using internal flubber-compatible interpolation
 * - Configurable animation delay
 * - Loops through all provided paths
 * - Supports gradient or inherited color fill
 *
 * @example
 * <SVGMorph
 *   paths={[sharpPath, roundPath, sharpPath]}
 *   delay={0.5}
 *   gradientId="unique-gradient"
 * />
 */
function SVGMorph({ delay, paths, inheritColor = false }: SVGMorphProps) {
  const [pathIndex, setPathIndex] = useState(0);
  const progress = useMotionValue(0);

  const arrayOfIndex = useMemo(() => paths.map((_, i) => i), [paths]);
  const path = useTransform(progress, arrayOfIndex, paths, {
    mixer: (a, b) => interpolate(a, b, { maxSegmentLength: 1 }),
  });

  useEffect(() => {
    const animation = animate(progress, pathIndex, {
      duration: 0.8,
      ease: [0.5, 0, 0.65, 1],
      delay: delay,
      onComplete: () => {
        if (pathIndex === paths.length - 1) {
          progress.set(0);
          setPathIndex(1);
        } else {
          setPathIndex(pathIndex + 1);
        }
      },
    });
    return () => {
      animation.stop();
    };
  }, [pathIndex, paths.length, progress, delay]);

  return (
    <motion.path
      fill={inheritColor ? "currentColor" : `url(#${GRADIENT_ID})`}
      d={path}
    />
  );
}

/**
 * Props for the AiMarkIconAnimatedSegment component
 * @property {boolean} [animate] - Whether to animate; triggers morph (and spin if enabled). When false, renders a static plain SVG.
 * @property {number} [delay] - Delay in seconds before starting the animation
 * @property {string} [className] - CSS class name for styling
 * @property {boolean} [inheritColor] - Whether to inherit color from parent instead of using gradient
 * @property {boolean} [spin] - Whether to enable the rotation animation in addition to the morph pulse
 */
interface AiMarkIconAnimatedSegmentProps {
  animate?: boolean;
  delay?: number;
  className?: string;
  inheritColor?: boolean;
  spin?: boolean;
}

/**
 * AiMarkIconAnimatedSegment renders a single animated star icon with rotation and morph effects.
 *
 * Features:
 * - Animates rotation on hover when spin is enabled
 * - Morphs between sharp and round star shapes
 * - Supports gradient or inherited color
 * - Unique gradient IDs to prevent conflicts
 * - Optional spin to add rotation on top of the morph pulse
 *
 * @example
 * <AiMarkIconAnimatedSegment animate={true} delay={0.15} />
 */
const AiMarkIconAnimatedSegment = memo(
  ({
    animate = false,
    delay,
    className,
    inheritColor = false,
    spin = false,
  }: AiMarkIconAnimatedSegmentProps) => {
    const fill = inheritColor ? "currentColor" : `url(#${GRADIENT_ID})`;
    const { mode } = useTheme();

    const gradientDef = !inheritColor && (
      <defs>
        <linearGradient
          id={GRADIENT_ID}
          x1="0.4962"
          y1="0.2483"
          x2="-0.0543"
          y2="0.4319"
          gradientUnits="objectBoundingBox"
        >
          <stop
            stopColor={mode === "dark" ? "#77C5EA" : "#026EE4"}
            style={{
              stopColor: mode === "dark" ? "#77C5EA" : "#026EE4",
              stopOpacity: 1,
            }}
          />
          <stop
            offset="1"
            stopColor={mode === "dark" ? "#D0D8E1" : "#27AEEE"}
            style={{
              stopColor: mode === "dark" ? "#D0D8E1" : "#27AEEE",
              stopOpacity: 1,
            }}
          />
        </linearGradient>
      </defs>
    );

    if (!animate) {
      return (
        <svg className={className} style={SVG_STYLE} viewBox="0 0 24 24">
          {gradientDef}
          <path fill={fill} d={SHARP_PATH} />
        </svg>
      );
    }

    return (
      <motion.svg
        className={className}
        style={SVG_STYLE}
        viewBox="0 0 24 24"
        animate={spin ? { rotate: 180 } : {}}
        transition={{
          duration: 2,
          type: "spring",
          repeat: 0,
          repeatType: "loop",
          repeatDelay: 1.5,
          delay,
        }}
      >
        {gradientDef}
        <SVGMorph
          paths={[SHARP_PATH, ROUND_PATH, SHARP_PATH]}
          delay={delay}
          inheritColor={inheritColor}
        />
      </motion.svg>
    );
  },
);

AiMarkIconAnimatedSegment.displayName = "AiMarkIconAnimatedSegment";

/**
 * Props for the AiMarkIconAnimated component
 * @property {boolean} [animate] - Whether to trigger animations (on hover or focus)
 * @property {number} [size] - Size of the icon in pixels
 * @property {boolean} [inheritColor] - Whether to inherit color from parent instead of using gradient
 * @property {boolean} [spin] - Whether to enable the rotation animation in addition to the morph pulse
 */
interface AiMarkIconAnimatedProps {
  animate?: boolean;
  size?: number;
  inheritColor?: boolean;
  spin?: boolean;
}

/**
 * AiMarkIconAnimated renders two animated Atlas AI star icons with rotation and morph effects.
 *
 * Features:
 * - Displays two stars in different sizes positioned in a composition
 * - Animates morphing on hover
 * - Supports rotation animation via spin prop
 * - Supports gradient or inherited color
 * - Automatically respects reduced motion preferences
 * - Configurable size
 * - Unique gradient IDs prevent conflicts when multiple icons render
 *
 * @example
 * <AiMarkIconAnimated animate={isActive} size={24} />
 */
export const AiMarkIconAnimated = ({
  animate = false,
  size = 16,
  inheritColor = false,
  spin = false,
}: AiMarkIconAnimatedProps) => {
  const { prefersReducedMotion } = usePrefersReducedMotion();
  const shouldAnimate = animate && !prefersReducedMotion;
  const largeSvgSize = size * 0.75;
  const smallSvgSize = (size * 6) / 16;

  const containerStyle = useMemo(
    () => ({ position: "relative" as const, width: size, height: size }),
    [size],
  );
  const largeIconStyle = useMemo(
    () => ({
      position: "absolute" as const,
      bottom: size / 16,
      left: size / 16,
      width: largeSvgSize,
      height: largeSvgSize,
    }),
    [largeSvgSize, size],
  );
  const smallIconStyle = useMemo(
    () => ({
      position: "absolute" as const,
      top: size / 16,
      right: size / 16,
      width: smallSvgSize,
      height: smallSvgSize,
    }),
    [smallSvgSize, size],
  );

  return (
    <div style={containerStyle} data-anv="ai-mark-icon-animated">
      <div style={largeIconStyle}>
        <AiMarkIconAnimatedSegment
          animate={shouldAnimate}
          delay={0.15}
          inheritColor={inheritColor}
          spin={spin}
        />
      </div>
      <div style={smallIconStyle}>
        <AiMarkIconAnimatedSegment
          animate={shouldAnimate}
          inheritColor={inheritColor}
          spin={spin}
        />
      </div>
    </div>
  );
};
